import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { createAdmin, getAdmins } from "@/api/admins";
import { HttpError } from "@/api/client";
import { PageHeader } from "@/components/PageHeader";
import { formatDate } from "@/lib/format";

export function AdminsPage() {
  const queryClient = useQueryClient();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admins"],
    queryFn: getAdmins,
  });

  const createAdminMutation = useMutation({
    mutationFn: createAdmin,
    onSuccess: async () => {
      setUsername("");
      setPassword("");
      setFormError(null);
      await queryClient.invalidateQueries({ queryKey: ["admins"] });
    },
  });

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);

    try {
      await createAdminMutation.mutateAsync({ username, password });
    } catch (err) {
      if (err instanceof HttpError) {
        setFormError(err.message);
      } else {
        setFormError("Admin creation failed. Please try again.");
      }
    }
  }

  if (isLoading) return <p className="empty-state">Loading…</p>;
  if (isError) {
    return (
      <div className="callout danger">
        <strong>Could not load admins</strong>
        <p>{error instanceof Error ? error.message : "Unknown error"}</p>
      </div>
    );
  }
  if (!data) return null;

  return (
    <>
      <PageHeader
        title="Admins"
        lede="Manage internal admin accounts. Admin users are created here only and do not appear in public registration."
        actions={
          <div className="stats-row">
            <article className="stat-card">
              <span>Admins</span>
              <strong>{data.summary.admins}</strong>
            </article>
            <article className="stat-card">
              <span>Active admins</span>
              <strong>{data.summary.active_admins}</strong>
            </article>
          </div>
        }
      />

      <div className="grid two-up">
        <section className="panel">
          <div className="panel-head">
            <div>
              <h2>Admin list</h2>
              <p>Current internal admin accounts in the system.</p>
            </div>
            <p>{data.admins.length} records</p>
          </div>
          {data.admins.length ? (
            <div className="table-shell">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Admin</th>
                    <th>Registered</th>
                    <th>Flight count</th>
                    <th>Last flight</th>
                  </tr>
                </thead>
                <tbody>
                  {data.admins.map((admin) => (
                    <tr key={admin.id}>
                      <td>
                        <div className="directory-user-cell">
                          <strong>{admin.username}</strong>
                          <span className="directory-role-badge directory-role-badge-admin">
                            Admin
                          </span>
                        </div>
                        <small>ID {admin.id}</small>
                      </td>
                      <td>{formatDate(admin.created_at)}</td>
                      <td>{admin.flight_count}</td>
                      <td>{formatDate(admin.last_flight_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="empty-state">No admin accounts exist yet.</p>
          )}
        </section>

        <section className="panel">
          <div className="panel-head">
            <div>
              <h2>Create admin</h2>
              <p>Only an existing admin can provision another admin account.</p>
            </div>
          </div>

          {formError && (
            <div className="callout danger">
              <strong>Could not create admin</strong>
              <p>{formError}</p>
            </div>
          )}

          <form className="upload-form" onSubmit={handleSubmit}>
            <label className="file-input">
              <span>Username</span>
              <input
                type="text"
                name="username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                placeholder="min. 3 characters"
                required
                minLength={3}
                autoComplete="username"
              />
            </label>
            <label className="file-input">
              <span>Password</span>
              <input
                type="password"
                name="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="min. 8 characters"
                required
                minLength={8}
                autoComplete="new-password"
              />
            </label>
            <button
              className="button primary"
              type="submit"
              disabled={createAdminMutation.isPending}
            >
              {createAdminMutation.isPending ? "Creating admin…" : "Create admin"}
            </button>
          </form>
        </section>
      </div>
    </>
  );
}
